import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useMemo, useState } from "react";
import { Alert, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { ConfirmationModel } from "../../components/features/common";
import { ResponsiveBreadcrumb } from "../../components/Layout/ResponsiveBreadcrumb";
import { ColumnRender, FilterType, IColumn, IFilter, IPagedListAction, SortOrder } from "../../components/UI/Declarations";
import { PagedList } from "../../components/UI/PagedList/PagedList";
import { technicalCompetenceSchemesService } from "../../services/reference/technicalCompetenceSchemes.service";
import { addEntityActionLabel, buildRecordLifecycleConfirmProps, EntityLabels, formatReferenceSourceDisplay, ListPageTitles, recordLifecycleListConfirmBodies, resolveActionErrorMessage, showToastSuccess, appRoutes } from "../../utils";
import {
    buildTcAdminPagedListActions,
    getTcAdminActionSuccessMessage,
    type TcAdminConfirmKind,
} from "./tcAdminListActions";

type ConfirmKind = TcAdminConfirmKind | null;

const TechnicalCompetenceSchemesListPage = () => {
    const navigate = useNavigate();

    const [refreshNumber, setRefreshNumber] = useState(0);
    const [actionError, setActionError] = useState<string | null>(null);
    const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
    const [confirmTcId, setConfirmTcId] = useState<string | null>(null);
    const [confirmTcLabel, setConfirmTcLabel] = useState("");

    const closeConfirm = () => {
        setConfirmKind(null);
        setConfirmTcId(null);
        setConfirmTcLabel("");
    };

    const runConfirmedAction = () => {
        if (!confirmTcId || !confirmKind) return;
        const id = confirmTcId;
        const op =
            confirmKind === "disable"
                ? technicalCompetenceSchemesService.disable(id)
                : confirmKind === "enable"
                  ? technicalCompetenceSchemesService.enable(id)
                  : confirmKind === "softDelete"
                    ? technicalCompetenceSchemesService.softDelete(id)
                    : technicalCompetenceSchemesService.restore(id);
        op.then(() => {
            showToastSuccess(getTcAdminActionSuccessMessage(confirmKind, confirmTcLabel));
            setActionError(null);
            setRefreshNumber((n) => n + 1);
            closeConfirm();
        }).catch((e: unknown) => {
            setActionError(resolveActionErrorMessage(e));
            closeConfirm();
        });
    };

    const openConfirm = (kind: ConfirmKind, item: { id: string; code: string }) => {
        setConfirmKind(kind);
        setConfirmTcId(item.id);
        setConfirmTcLabel(item.code);
    };

    const confirmProps = useMemo(
        () =>
            buildRecordLifecycleConfirmProps(
                confirmKind,
                EntityLabels.technicalCompetenceScheme,
                confirmTcLabel,
                recordLifecycleListConfirmBodies(confirmTcLabel)
            ),
        [confirmKind, confirmTcLabel]
    );

    const columns: IColumn[] = useMemo(
        () => [
            {
                member: "code",
                renderAs: ColumnRender.customRenderer,
                title: "Scheme value",
                width: 160,
                customRenderer: (item: { code?: string }) => (
                    <td className="text-start">
                        <span className="font-monospace">{item.code ?? ""}</span>
                    </td>
                ),
            } as IColumn,
            { member: "description", renderAs: ColumnRender.text, title: "Description", width: 320 } as IColumn,
            {
                member: "source",
                renderAs: ColumnRender.customRenderer,
                title: "Source",
                width: 120,
                customRenderer: (item: { source?: string }) => (
                    <td className="text-start">{formatReferenceSourceDisplay(item.source)}</td>
                ),
            } as IColumn,
            { member: "isActive", renderAs: ColumnRender.checkCross, title: "Active", width: 90, align: "center" } as IColumn,
        ],
        []
    );

    const filters: IFilter[] = useMemo(
        () => [
            { title: "Scheme value", member: "code", type: FilterType.text } as IFilter,
            { title: "Description", member: "description", type: FilterType.text } as IFilter,
            { title: "Source", member: "source", type: FilterType.text } as IFilter,
            {
                title: "Active",
                member: "isActive",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
            } as IFilter,
            {
                title: "Deleted",
                member: "isDeleted",
                type: FilterType.select,
                options: { All: null, Yes: true, No: false },
                defaultValue: false,
            } as IFilter,
        ],
        []
    );

    const itemActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: "Edit",
                onClick: (item: { id: string }) => navigate(`/technical-competence-schemes/${item.id}`),
                primary: true,
            } as IPagedListAction,
            ...buildTcAdminPagedListActions((kind, item) => openConfirm(kind, item)),
        ],
        [navigate]
    );

    const headActions: IPagedListAction[] = useMemo(
        () => [
            {
                text: addEntityActionLabel(EntityLabels.technicalCompetenceScheme),
                icon: faPlus,
                onClick: () => navigate("/technical-competence-schemes/new"),
                primary: true,
            } as IPagedListAction,
        ],
        [navigate]
    );

    return (
        <Container fluid>
            <Helmet>
                <title>{ListPageTitles.technicalCompetenceSchemes}</title>
            </Helmet>
            <Row className="mb-3">
                <Col>
                    <ResponsiveBreadcrumb
                        items={[
                            { text: EntityLabels.referenceData, to: appRoutes.codes },
                            { text: ListPageTitles.technicalCompetenceSchemes },
                        ]}
                    />
                    <h1>{ListPageTitles.technicalCompetenceSchemes}</h1>
                    <p className="text-muted">
                        Technical competence schemes for regulatory reporting. Administrators can add, edit, disable, or remove entries.
                    </p>
                    {actionError && (
                        <Alert variant="danger" className="mt-2" onClose={() => setActionError(null)} dismissible>
                            {actionError}
                        </Alert>
                    )}
                </Col>
            </Row>
            <Row>
                <Col>
                    <PagedList
                        columns={columns}
                        filters={filters}
                        headActions={headActions}
                        itemActions={itemActions}
                        rowActionsAriaLabel="Technical competence scheme actions"
                        showDeletedRowIndicator
                        defaultSortBy="Code"
                        defaultSortOrder={SortOrder.ascending}
                        getUrl="/api/referencedata/technical-competence-schemes/all"
                        rowFormatter={() => null}
                        persistanceKey="TechnicalCompetenceSchemesListPage"
                        refreshNumber={refreshNumber}
                    />
                </Col>
            </Row>

            <ConfirmationModel
                show={confirmKind !== null}
                onClose={(confirmed) => {
                    if (confirmed) runConfirmedAction();
                    else closeConfirm();
                }}
                title={confirmProps.title}
                body={confirmProps.body}
                buttonSuccessVariant={confirmProps.buttonSuccessVariant}
                buttonSuccessText={confirmProps.buttonSuccessText}
                buttonCancelText="Cancel"
            />
        </Container>
    );
};

export { TechnicalCompetenceSchemesListPage };
